import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Modal } from '../../Smart/Modal/Modal';
import { HoneycombTasks } from '../HoneycombTasks';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { CompanyReactContext } from '../../../contexts/CompanyContext';
import { signDocTypeToFeatureFlagMap } from '../../SignDoc/SignDocLib';
import { CreateEditNewSignDocModal } from '../../LearningManagement/CreateNewSignDocButton/CreateEditNewSignDocModal/CreateEditNewSignDocModal';
import './HoneycombModal.scss';
import AILogo from '../../HiveComponent/AILogo/AILogo';
import { HiveComponentPossibleSizes, HiveComponentPossibleStatuses } from '../../HiveComponent/HiveComponent';
import useSignDocTypes from '../../../Hooks/useSignDocTypes';
import { SignDocListRefresherContext } from '../../../contexts/SignDocListRefresherContext';
import { isEmpty } from 'lodash';

/**
 * AI badge component for honeycomb tasks
 * @returns {JSX.Element} AI badge icon
 */
const AIBadge = ({ isAI }) => (
    <div className="ai-badge">
        <AILogo isAI={isAI} size="100%" className="ai-badge-img" />
    </div>
);

const defaultSize = HiveComponentPossibleSizes.large;

/**
 * HoneycombModal component displays a modal with a honeycomb grid of service options
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the modal is open
 * @param {Function} props.onClose Function to call when modal is closed
 * @returns {JSX.Element} Modal with honeycomb grid
 */
export const HoneycombModal = ({ open, onClose }) => {
    const { i18n } = useContext(I18nReactContext);
    const { featureFlags } = useContext(CompanyReactContext);
    const { refreshSignDocList } = useContext(SignDocListRefresherContext);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedSignDocType, setSelectedSignDocType] = useState(null);
    const [honeycombServices, setHoneycombServices] = useState([]);

    // Use the hook to access sign doc configurations with translations
    const { 
        signDocTypes,
        getHoneycombText, 
        getHoneycombIcon, 
        getHoneycombSecondaryIcon,
        getIsAIImplemented,
        getHoneycombColumnPosition,
        getHoneycombRowPosition,
        getSignDocOptionsTypesForCreateEditSignDocModal
    } = useSignDocTypes();

    // Define honeycomb services with memoization to prevent rebuilding on every render
    const honeycombServicesData = useMemo(() => {
        // Create services array by mapping through the available configurations
        return signDocTypes.map((type) => {
            return {
                id: type,
                columnPosition: getHoneycombColumnPosition({ type }),
                rowPosition: getHoneycombRowPosition({ type }),
                text: getHoneycombText({ type }),
                icon: getHoneycombIcon({ type }),
                secondaryIcon: getHoneycombSecondaryIcon({ type }) ? <AIBadge isAI={getIsAIImplemented({ type })} /> : null,
                signDocType: type,
                size: defaultSize,
                signDocOptionsTypes: getSignDocOptionsTypesForCreateEditSignDocModal({ type }),
            };
        }).filter(service => service.columnPosition != null && service.rowPosition != null); // Filter out services not included in the grid
    }, [signDocTypes, getHoneycombText, getHoneycombIcon, getHoneycombSecondaryIcon, getIsAIImplemented, getHoneycombColumnPosition, getHoneycombRowPosition, getSignDocOptionsTypesForCreateEditSignDocModal]);

    // Process services to add status, onClick, and isInteractive based on feature flags
    const processHoneycombServices = useCallback(({ services }) => {
        return services.map(service => {
            // Check if the feature is available
            let signDocFeatureFlag = signDocTypeToFeatureFlagMap[service.signDocType];
            const isFeatureAvailable = featureFlags[signDocFeatureFlag] || false;

            // Set status based on feature availability
            const status = isFeatureAvailable ? HiveComponentPossibleStatuses.active : HiveComponentPossibleStatuses.locked;

            // Set onClick handler only if feature is available
            const onClick = isFeatureAvailable
                ? () => handleOpenCreateModal({ signDocType: service.signDocType })
                : undefined;

            // Return the processed service
            return {
                ...service,
                status,
                onClick,
                isInteractive: isFeatureAvailable
            };
        });
    }, [featureFlags]);

    // Initialize by processing the services
    useEffect(() => {
        // Only process services if feature flags are loaded
        if (isEmpty(featureFlags)) {
            return;
        }

        const processedServices = processHoneycombServices({ services: honeycombServicesData });
        setHoneycombServices(processedServices);
    }, [honeycombServicesData, featureFlags, processHoneycombServices]);

    // Handle opening the create/edit modal
    const handleOpenCreateModal = ({ signDocType }) => {
        setSelectedSignDocType(signDocType);
        setCreateModalOpen(true);
    };

    // Handle closing the create/edit modal
    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
        setSelectedSignDocType(null);
    };

    const handleAfterEditComplete = () => {
        refreshSignDocList();
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <div className="HoneycombModal modalWrapper">
                    <div className="modalTitle">
                        {i18n.translate('LearningManagementPage.honeycombTitle')}
                    </div>
                    <div className="modalContent modalContentHoneycomb">
                        <HoneycombTasks 
                            services={honeycombServices} 
                            defaultSize={defaultSize}
                        />
                    </div>
                </div>
            </Modal>

            {createModalOpen && (
                <CreateEditNewSignDocModal
                    newDocSignModalOpen={createModalOpen}
                    closeNewDocSignModal={handleCloseCreateModal}
                    signDoc={null}
                    editMode={false}
                    signDocType={selectedSignDocType}
                    SignDocOptionsTypes={honeycombServices.find(service => service.signDocType === selectedSignDocType)?.signDocOptionsTypes}
                    onAfterEditComplete={handleAfterEditComplete}
                    forKnowledgeCenter={false}
                    forceCompletion={false}
                />
            )}
        </>
    );
};

export default HoneycombModal;
